import { defineStore } from 'pinia'
import { ref } from 'vue'
import portalModeService, { type CompanyPortalChannels } from '@/services/portalmode.service'

export const usePortalModeStore = defineStore('portalMode', () => {
  const currentSelection = ref('broker')
  const isFetchingPortalMode = ref(false)
  const hasMultiplePortalModes = ref(false)
  const availableChannels = ref([
    {
      key: 'hasBroker',
      name: 'Wholesale',
      value: 'broker',
      icon: 'fas fa-handshake'
    },
    {
      key: 'hasMiniCor',
      name: 'Non-delegated',
      value: 'minicorrespondent',
      icon: 'fas fa-building'
    },
    {
      key: 'hasCor',
      name: 'Delegated',
      value: 'correspondent',
      icon: 'fas fa-university'
    }
  ])
  const channelList = ref<{ name: string; value: string; icon: string }[]>([])

  const setCurrentSelection = (selection: string) => {
    currentSelection.value = selection
  }

  const setIsFetchingPortalMode = (isFetching: boolean) => {
    isFetchingPortalMode.value = isFetching
  }

  const setAvailableChannels = (channels: CompanyPortalChannels) => {
    channelList.value = []
    availableChannels.value.forEach(channel => {
      if (channels[channel.key as keyof CompanyPortalChannels]) {
        channelList.value.push({
          name: channel.name,
          value: channel.value,
          icon: channel.icon
        })
      }
    })

    setHasMultiplePortalModes(channelList.value.length > 1)
  }

  const setHasMultiplePortalModes = (multiplePortalModes: boolean) => {
    hasMultiplePortalModes.value = multiplePortalModes
  }

  const fetchPortalMode = async () => {
    isFetchingPortalMode.value = true
    const response = await portalModeService.getPortalMode()
    if (response) {
      setCurrentSelection(response.currentSelection)
    }
    isFetchingPortalMode.value = false
  }

  const updatePortalMode = async (channel: string, hashedCompanyId: string, company: string) => {
    const response = await portalModeService.updatePortalMode({ channel, hashedCompanyId, company })
    setCurrentSelection(response.currentSelection)
  }

  const syncPortalModeWithCompany = async () => {
    const response = await portalModeService.syncPortalModeWithCompany()
    if (response?.data?.currentSelection) {
      setCurrentSelection(response.data.currentSelection)
    }
    if (response?.company) {
      setAvailableChannels(response.company)
    }
    return response
  }

  return {
    currentSelection,
    isFetchingPortalMode,
    hasMultiplePortalModes,
    availableChannels,
    channelList,
    setCurrentSelection,
    setIsFetchingPortalMode,
    fetchPortalMode,
    updatePortalMode,
    syncPortalModeWithCompany,
    setHasMultiplePortalModes,
    setAvailableChannels,
  }
})